import { create } from 'zustand';

/**
 * Tiny pub/sub so an external actor (e.g. Case Partner in the side panel) can tell an
 * open screen that a resource changed, without a feature-wide websocket.
 *
 * Keys are `${kind}:${id}` so ids from different screens cannot collide. The stored
 * value is a monotonically increasing counter so repeated edits each trigger a refresh.
 */
export type UiChangeKind = 'bundle' | 'deadline';

export type UiChange = {
  kind: string;
  id: string;
};

type UiChangeState = {
  signals: Record<string, number>;
  notifyUiChanged: (kind: string, id: string) => void;
};

export const uiChangeKey = (kind: string, id: string): string => `${kind}:${id}`;

export const useUiChangeStore = create<UiChangeState>((set) => ({
  signals: {},
  notifyUiChanged: (kind, id) => {
    const key = uiChangeKey(kind, id);
    set((state) => ({
      signals: { ...state.signals, [key]: (state.signals[key] ?? 0) + 1 },
    }));
  },
}));

export const notifyUiChanged = (kind: string, id: string): void => {
  useUiChangeStore.getState().notifyUiChanged(kind, id);
};

export const useUiChangeSignal = (kind: string, id: string | undefined): number =>
  useUiChangeStore((state) => (id ? state.signals[uiChangeKey(kind, id)] ?? 0 : 0));

/** Increments when any resource of this kind changes, so list screens can refresh. */
export const useUiChangeKindSignal = (kind: string): number =>
  useUiChangeStore((state) => {
    const prefix = `${kind}:`;
    let total = 0;
    for (const [key, value] of Object.entries(state.signals)) {
      if (key.startsWith(prefix)) total += value;
    }
    return total;
  });

export function normalizeUiChanges(metadata?: Record<string, unknown> | null): UiChange[] {
  if (!metadata) return [];
  const out: UiChange[] = [];
  const raw = metadata.ui_changes;
  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (!item || typeof item !== 'object') continue;
      const row = item as Record<string, unknown>;
      const kind = String(row.kind || '').trim();
      const changeId = String(row.id || '').trim();
      if (kind && changeId) out.push({ kind, id: changeId });
    }
  }
  return out;
}

export function mergeUiChanges(existing: UiChange[] | undefined, incoming: UiChange[]): UiChange[] {
  const seen = new Set((existing || []).map((change) => uiChangeKey(change.kind, change.id)));
  const next = [...(existing || [])];
  for (const change of incoming) {
    const key = uiChangeKey(change.kind, change.id);
    if (seen.has(key)) continue;
    seen.add(key);
    next.push(change);
  }
  return next;
}
